import { abs, dot, float, max, mix, mx_noise_float, normalize, sin, smoothstep, vec3 } from 'three/tsl';
import {
  altitudeZones,
  type BakedNoise,
  beaches,
  canopyCrowns,
  creaseShade,
  detailWeights,
  faded,
  forestEdge,
  linear,
  NOISE_MEANS,
  noiseTerm,
  noiseWhen,
  reliefShade,
  resolved,
  ridgeOf,
  rockRibs,
  rockStreaks,
  strata,
  type TerrainStyle,
} from './TerrainStyle';

type N = any;
type RGB = [number, number, number];

/**
 * A biome's ground, as data: the parametric palette (biomeStyle) turns it into a TerrainStyle.
 * Heights are as the palette sees them (see TerrainProfile.heightScale).
 */
export interface BiomePalette {
  /** Two ground tones, drifting between each other over a kilometre or so. */
  ground: [RGB, RGB];
  /** A second pair the ground shifts toward region by region (optional). */
  groundAlt?: [RGB, RGB];
  /** Dry, pale patches: their colour and how much of the ground they take (0 … 1). */
  dry?: { color: RGB; amount: number };
  /** Flowers or speckles (up to three colours) and how many (0 … 1). */
  flowers?: { colors: RGB[]; amount: number };
  /** Forests as seen from afar: canopy colours, the share of the land they cover, their size (m). */
  forest?: { canopy: [RGB, RGB]; cover: number; wavelength: number };
  /** Bare earth where slopes steepen. */
  dirt: RGB;
  /** Banded rock on cliffs: dark and light layers. */
  rock: [RGB, RGB];
  /** Shores. */
  sand: RGB;
  /** Snow above this height (m, as the palette sees heights), and its colour. */
  snow?: { line: number; color?: RGB };
  /** Wind ripples across the ground (dunes, 0 … 1). */
  ripples?: number;
  /** Light the ground gives off: its colour, pattern and strength. */
  glow?: { color: RGB; kind: 'specks' | 'veins' | 'patches'; strength: number };
  /** Ground roughness (rock is rougher). */
  roughness?: number;
  /** What the ground itself is, close up (its detail texture): soil and grass by default. */
  surface?: 'ground' | 'sand' | 'snow';
}

/** The forest noise's spread with its ragged edges added: 0.265 × √(1 + 0.45² + 0.22²). */
const FOREST_SD = 0.265 * Math.sqrt(1 + 0.45 * 0.45 + 0.22 * 0.22);

/**
 * The palette of a biome given as data (BiomePalette). Every noise term fades toward its mean
 * where a pixel spans too much ground to show it (ctx.footprint), as Verdant's does.
 * `glowPhase` is the world's glow at a hollow-local point (LightRig.glowAt), for glowing ground.
 */
export function biomeStyle(def: BiomePalette, glowPhase?: (p: N) => N): TerrainStyle {
  const forest = def.forest;
  const snowLine = def.snow?.line;
  // The forest noise's threshold for this much cover (its spread with the ragged edges added).
  const lo = forest ? FOREST_SD * inverseNormal(1 - Math.min(Math.max(forest.cover, 0.01), 0.99)) - 0.13 : 0;
  // Its broad noise terms, for the tiles to carry (see TerrainStyle.bakedNoise): the commonest
  // first, as a world of several biomes keeps only the first 8 of all of theirs (mixStyles).
  const bakedNoise: BakedNoise[] = [{ wavelength: 1400 }, { wavelength: 600, offset: [7.3, 2.9, 5.6] }];
  if (forest) {
    bakedNoise.push({ wavelength: forest.wavelength, offset: [3.1, 7.7, 1.3], forest: true });
    bakedNoise.push({ wavelength: forest.wavelength * 0.26, offset: [8.2, 1.9, 4.4], forest: true });
  }
  if (def.flowers && def.flowers.colors.length > 0)
    bakedNoise.push({ wavelength: 850, offset: [6.2, 1.4, 3.3] });
  if (def.groundAlt) bakedNoise.push({ wavelength: 3200, offset: [5.5, 2.1, 8.7] });
  if (def.glow && def.glow.kind !== 'specks' && def.glow.kind !== 'veins')
    bakedNoise.push({ wavelength: 1300, offset: [0.7, 3.3, 9.1] });
  const style: TerrainStyle = {
    bakedNoise,
    forestMask(ctx) {
      if (!forest) return float(0);
      const { p, h, slope } = ctx;
      const t = noiseTerm(ctx, p, forest.wavelength, [3.1, 7.7, 1.3]);
      // Ragged edges (finer noise bends the border in and out), creeping up the gullies.
      let n: N = t.n.add(linear(noiseTerm(ctx, p, forest.wavelength * 0.26, [8.2, 1.9, 4.4])).mul(0.45));
      if (ctx.ridge) n = n.add(ridgeOf(ctx).all.mul(-0.2));
      n = n.add(forestEdge(ctx, p, forest.wavelength * 0.065, [2.6, 6.1, 9.8], n));
      let m: N = faded(t, forest.cover, smoothstep(lo, lo + 0.26, n)).mul(
        float(1).sub(smoothstep(0.2, 0.4, slope)),
      );
      if (snowLine !== undefined) m = m.mul(float(1).sub(smoothstep(snowLine * 0.55, snowLine * 0.8, h)));
      return m;
    },

    land(ctx) {
      const { p, h, slope, near, mid } = ctx;
      const macroT = noiseTerm(ctx, p, 1400);
      const fineT = noiseTerm(ctx, p, 95);
      const patchT = noiseTerm(ctx, p, 11);
      const macro = linear(macroT);
      const fine = linear(fineT);
      const patch = linear(patchT);
      const grain = noiseWhen(p.mul(1 / 1.7), near).mul(near);

      let ground: N = mix(vec3(...def.ground[0]), vec3(...def.ground[1]), macro.mul(0.5).add(0.5));
      if (def.groundAlt) {
        const regionT = noiseTerm(ctx, p, 3200, [5.5, 2.1, 8.7]);
        const alt = mix(vec3(...def.groundAlt[0]), vec3(...def.groundAlt[1]), fine.mul(0.5).add(0.5));
        ground = mix(ground, alt, faded(regionT, 0.5, smoothstep(-0.15, 0.2, regionT.n)));
      }
      if (def.dry) {
        const share = faded(
          patchT,
          NOISE_MEANS.dry * 30 * def.dry.amount,
          smoothstep(
            0.35 - 0.3 * def.dry.amount,
            0.75 - 0.3 * def.dry.amount,
            patchT.n.mul(0.6).add(fineT.n.mul(0.4)),
          ),
        );
        ground = mix(ground, vec3(...def.dry.color), share.mul(0.6));
      }
      ground = ground.mul(grain.mul(0.18).add(1)).mul(patch.mul(0.08).add(1));
      if (snowLine !== undefined) ground = altitudeZones(ground, h.add(fine.mul(40)), snowLine, !!forest);
      if (def.ripples) {
        // Dune ripples: bands a few metres apart, bending with the ground's noise, fading off.
        const dir = vec3(0.73, 0.21, 0.65);
        const phase = dot(p, dir)
          .mul(1 / 3.1)
          .add(fine.mul(9))
          .add(patch.mul(2));
        const ripple = sin(phase).mul(float(resolved(ctx, 20)).mul(near.mul(0.7).add(mid.mul(0.3))));
        ground = ground.mul(ripple.mul(0.07 * def.ripples).add(1));
      }
      if (def.flowers && def.flowers.colors.length > 0) {
        const cols = def.flowers.colors;
        const kindN = noiseWhen(p.mul(0.13).add(vec3(9.1, 2.2, 4.4)), near);
        let flowerCol: N = vec3(...cols[0]);
        if (cols[1]) flowerCol = mix(flowerCol, vec3(...cols[1]), smoothstep(-0.2, 0.2, kindN));
        if (cols[2]) flowerCol = mix(flowerCol, vec3(...cols[2]), smoothstep(0.1, 0.4, kindN));
        const a = def.flowers.amount;
        const speckle = smoothstep(0.66 - 0.12 * a, 0.7 - 0.1 * a, noiseWhen(p.mul(6.5), near));
        ground = mix(ground, flowerCol, speckle.mul(near.mul(near)).mul(0.6));
        // Seen from further off, fields of them tint the ground.
        const fieldT = noiseTerm(ctx, p, 850, [6.2, 1.4, 3.3]);
        const field = faded(fieldT, NOISE_MEANS.field * a * 2, smoothstep(0.2, 0.5, fieldT.n).mul(a));
        ground = mix(ground, flowerCol.mul(0.8), field.mul(float(1).sub(near).mul(0.35)));
      }
      // The landform (erosion): gullies hold water, soil and snow; crests are bare and dry.
      const ridge = ridgeOf(ctx);
      const gully = smoothstep(0.1, 0.65, ridge.all.negate());
      const crest = smoothstep(0.15, 0.7, ridge.all);
      if (def.dry) ground = mix(ground, vec3(...def.dry.color), crest.mul(0.25));
      let land: N = ground;
      if (forest) {
        const cover = this.forestMask(ctx);
        // Only worked out where there is forest.
        const canopyN = linear(noiseTerm(ctx, p, 160, undefined, cover)).mul(mid.mul(0.6).add(0.4));
        const canopy = mix(vec3(...forest.canopy[0]), vec3(...forest.canopy[1]), canopyN.mul(0.5).add(0.5));
        land = mix(land, canopy.mul(canopyCrowns(ctx, cover)), cover);
      }
      // Bare earth where slopes steepen, sand at the shore, banded rock on cliffs.
      land = mix(
        land,
        vec3(...def.dirt),
        smoothstep(0.1, 0.22, slope.add(patch.mul(0.04)).add(ridge.all.mul(0.05))).mul(0.6),
      );
      const sandM = float(1)
        .sub(smoothstep(1.2, 4.5, h.add(fine.mul(1.5))))
        .mul(beaches(ctx));
      land = mix(land, vec3(...def.sand), sandM);
      const rock = mix(vec3(...def.rock[0]), vec3(...def.rock[1]), strata(ctx, fineT, patchT))
        .mul(grain.mul(0.25).add(1))
        .mul(fine.mul(0.15).add(1));
      // Above the trees (or anywhere on snowfields), the crests of steep ground are bare rock.
      const ribs = rockRibs(
        crest,
        slope,
        snowLine !== undefined
          ? smoothstep(snowLine - 250, snowLine - 60, h.add(fine.mul(60)))
          : float(def.surface === 'snow' ? 1 : 0),
      );
      const rocky = max(
        smoothstep(0.2, 0.38, slope.add(fine.mul(0.05)).add(grain.mul(0.02)).add(ridge.all.mul(0.09))),
        ribs,
      );
      land = mix(land, rock.mul(rockStreaks(ctx, rocky)), rocky);
      let snowMask: N = float(0);
      if (snowLine !== undefined) {
        // Lower and steeper down the gullies (couloirs); blown off the crests.
        snowMask = smoothstep(
          snowLine - 90,
          snowLine + 60,
          h.add(fine.mul(60)).add(gully.mul(70)).sub(crest.mul(40)),
        )
          .mul(float(1).sub(smoothstep(0.2, 0.34, slope.add(fine.mul(0.04)).sub(gully.mul(0.08)))))
          .mul(float(1).sub(ribs.mul(0.9)));
        land = mix(
          land,
          // A little under fresh snow's albedo, and matte (see Verdant's): real values dazzled.
          vec3(...(def.snow?.color ?? [0.79, 0.82, 0.855])).mul(grain.mul(0.05).add(1)),
          snowMask,
        );
      }
      // Gullies are shaded and damp (narrow ones read as dark creases), crests a little bleached.
      land = land
        .mul(float(1).sub(gully.mul(0.14)))
        .mul(creaseShade(ctx, ridge.narrow))
        .mul(crest.mul(0.1).add(1))
        .mul(reliefShade(ridge.relief));
      const r = def.roughness ?? 0.92;
      const roughness = mix(mix(float(r), float(0.8), rocky), float(0.7), snowMask);

      let emissive: N | undefined;
      if (def.glow) {
        const g = def.glow;
        let amount: N;
        if (g.kind === 'specks') {
          amount = smoothstep(0.78, 0.84, mx_noise_float(p.mul(1 / 3.2).add(vec3(1.1, 5.2, 2.3))))
            .mul(smoothstep(0.1, 0.5, mx_noise_float(p.mul(1 / 90))))
            .mul(mid.mul(0.5).add(0.5))
            .mul(1.6);
        } else if (g.kind === 'veins') {
          amount = smoothstep(0.9, 0.98, float(1).sub(abs(mx_noise_float(p.mul(1 / 12)))))
            .mul(rocky.mul(0.8).add(0.2))
            .mul(mid)
            .mul(0.6);
        } else {
          const fields = smoothstep(-0.1, 0.35, linear(noiseTerm(ctx, p, 1300, [0.7, 3.3, 9.1])));
          const spots = smoothstep(0.25, 0.55, mx_noise_float(p.mul(1 / 90).add(vec3(2.2, 0, 1.7))));
          amount = fields.mul(spots.mul(0.2).add(0.04)).mul(float(1).sub(rocky));
        }
        const phase = glowPhase ? glowPhase(normalize(p)) : float(1);
        emissive = vec3(...g.color)
          .mul(amount.mul(g.strength))
          .mul(phase);
      }
      const surface = def.surface === 'sand' ? 2 : def.surface === 'snow' ? 3 : 0;
      return { albedo: land, roughness, emissive, detail: detailWeights(sandM, rocky, snowMask, surface) };
    },

    water: { shallow: [0.06, 0.26, 0.28], deep: [0.01, 0.05, 0.09], depthScale: 22 },
  };
  return style;
}

/** Φ⁻¹(p): the standard normal quantile (Acklam's rational approximation, ~1e-9). */
export function inverseNormal(p: number): number {
  const a = [
    -39.69683028665376, 220.9460984245205, -275.9285104469687, 138.357751867269, -30.66479806614716,
    2.506628277459239,
  ];
  const b = [
    -54.47609879822406, 161.5858368580409, -155.6989798598866, 66.80131188771972, -13.28068155288572,
  ];
  const c = [
    -0.007784894002430293, -0.3223964580411365, -2.400758277161838, -2.549732539343734, 4.374664141464968,
    2.938163982698783,
  ];
  const d = [0.007784695709041462, 0.3224671290700398, 2.445134137142996, 3.754408661907416];
  const lo = 0.02425;
  if (p < lo) {
    const q = Math.sqrt(-2 * Math.log(p));
    return (
      (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
      ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1)
    );
  }
  if (p > 1 - lo) {
    const q = Math.sqrt(-2 * Math.log(1 - p));
    return (
      -(((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
      ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1)
    );
  }
  const q = p - 0.5;
  const r = q * q;
  return (
    ((((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * q) /
    (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1)
  );
}
